import React, { useContext, useState, useEffect } from "react";
import styles from "../dashboard/dashboard.module.scss";
import { connect } from "react-redux";
import { GanttChart } from "./component/GanttChart";
import {
  ArrowDownMedium,
  DayIcon,
  MonthIcon,
  QuarterIcon,
  ScopeViewIcon,
  WeekIcon,
  YearIcon,
} from "../../modules/default-svg";
import CustomButton from "../../components/controls/button/Button";
import CustomPopover from "../../components/popover/Popover";
import { SharedTimelineContext } from "./SharedTimelineContext";
import { Calendar, CalendarDays } from "lucide-react";
import KeyEventsPopover from "./components/KeyEventsPopover";
import ReportButton from "../../components/Export/ReportButton";
import ExportButton from "../../components/Export/ExportButton";
import CriticalPathButton from "../../components/Export/CriticalPathButton";
import { Tooltip } from "@fluentui/react-components";

// Entity types whose Timeline tab shows the Key Events feature (Roadmap/Timeline engine, generalized
// beyond Portfolio) - Scope/Task never get their own tab, so they're never an entityType here.
const KEY_EVENT_CAPABLE_TYPES = ["portfolio", "program", "projectTimeline"];
const EDIT_PERMISSION_BY_TYPE = {
  portfolio: "Portfolio.Edit",
  program: "Program.Edit",
  projectTimeline: "Project.Edit",
};
const EXPORT_CONFIG_BY_TYPE = {
  portfolio: { parentType: "Portfolio", parent: 1 },
  program: { parentType: "Program", parent: 2 },
  projectTimeline: { parentType: "Project", parent: 3 },
  // "project" (SummaryTimeLine, used by ProjectManagerDashboard's /dashboard mini-Gantt) is a
  // separate entityType from "projectTimeline" but still reports on a Project - without this entry
  // Report/Export silently fell back to the Program config and errored (wrong id sent to the wrong
  // parentType's endpoint).
  project: { parentType: "Project", parent: 3 },
};
// Excel-style outline levels: how many row depths each entity type's tree has (see
// SharedTimelineContext's setOutlineLevel). Portfolio/"project" stop at the nested item; Program/
// ProjectTimeline go one level deeper into Scope and TaskGroup.
const OUTLINE_LEVELS_BY_ENTITY_TYPE = {
  portfolio: 2,
  program: 4,
  projectTimeline: 4,
  project: 2,
};

const Timeline = ({ entityID, entityType, isFullHeight, setFullHeight }) => {
  const {
    setEntityID,
    setEntityType,
    tabView,
    setTabView,
    setTodayTrigger,
    timelineData,
    timelineKeyEvents,
    timelineDirectProjects,
    createKeyEvent,
    updateKeyEvent,
    deleteKeyEvent,
    keyEventsLoading,
    outlineLevel,
    setOutlineLevel,
    criticalPathHighlight,
    setCriticalPathHighlight,
  } = useContext(SharedTimelineContext);

  const [isHideSideBar, setIsHideSideBar] = useState(false);
  const [selection, setSelection] = useState({
    icon: <MonthIcon />,
    name: "Month",
  });
  const [openPopover, setIsOpenPopover] = useState(null);
  const [count, setCount] = useState(0);
  const [keyEventsOpen, setKeyEventsOpen] = useState(false);
  const [keyEvents, setKeyEvents] = useState([]);

  const isKeyEventCapable = KEY_EVENT_CAPABLE_TYPES.includes(entityType);

  // Check permissions
  const permissions = localStorage.getItem("permissions");
  const canEditEntity = isKeyEventCapable && permissions && permissions.includes(EDIT_PERMISSION_BY_TYPE[entityType]);

  useEffect(() => {
    if (entityID) {
      setEntityID(entityID);
      setEntityType(entityType);
    }
  }, [entityID, entityType]);

  useEffect(() => {
    if (timelineData && timelineData.length > 0 && isKeyEventCapable) {
      setKeyEvents(timelineKeyEvents);
    }
  }, [timelineData, entityType]);

  useEffect(() => {
    switch (tabView) {
      case "week":
        setSelection({ icon: <WeekIcon />, name: "Week" });
        break;
      case "day":
        setSelection({ icon: <DayIcon />, name: "Day" });
        break;
      case "quarter":
        setSelection({ icon: <QuarterIcon />, name: "Quarter" });
        break;
      case "year":
        setSelection({ icon: <YearIcon />, name: "Year" });
        break;
      default:
        setSelection({ icon: <MonthIcon />, name: "Month" });
        break;
    }
  }, [tabView]);

  return (
    <div className="d-flex flex-column gap-2 h-full" style={{ width: '100%' }}>
      <div
        className={styles.container}
        style={{ height: `calc(100vh - ${isFullHeight ? "190px" : "368px"})` }}
      >
        <div className={styles.secondHeader}>
          <div className={styles.content}>
            <CustomButton
              btnClass=""
              icon={<ScopeViewIcon isHide={isHideSideBar} />}
              clickHandler={() => {
                setIsHideSideBar(!isHideSideBar);
              }}
            />
            {OUTLINE_LEVELS_BY_ENTITY_TYPE[entityType] && (
              <div className={styles.outlineLevels}>
                {Array.from(
                  { length: OUTLINE_LEVELS_BY_ENTITY_TYPE[entityType] },
                  (_, i) => i + 1
                ).map((level) => (
                  <Tooltip content={`Show level ${level}`} relationship="label">
                    <button
                      key={level}
                      type="button"
                      className={`${styles.outlineLevelBtn} ${outlineLevel === level ? styles.outlineLevelBtnActive : ""}`}
                      onClick={() => setOutlineLevel(level)}
                    >
                      {level}
                    </button>
                  </Tooltip>

                ))}
              </div>
            )}
          </div>
          <div className={styles.secondaryContent}>

            <div className={styles.featuresContainer}>
              <ReportButton
                parentType={EXPORT_CONFIG_BY_TYPE[entityType]?.parentType ?? "Program"}
                parentId={entityID}
              />
              <ExportButton config={{ parent: EXPORT_CONFIG_BY_TYPE[entityType]?.parent ?? 2, parentId: entityID, category: 5 }} />
            </div>
            {isKeyEventCapable && canEditEntity && (
              <KeyEventsPopover
                open={keyEventsOpen}
                onOpenChange={setKeyEventsOpen}
                entityType={entityType}
                entityID={entityID}
                timelineData={timelineData}
                directProjects={timelineDirectProjects}
                keyEvents={keyEvents}
                onCreateEvent={createKeyEvent}
                onUpdateEvent={updateKeyEvent}
                onDeleteEvent={deleteKeyEvent}
                isLoading={keyEventsLoading}
              >
                <div
                  className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer transition-all"
                  style={{
                    border: '1px solid #342c82',
                    borderRadius: '20px',
                    backgroundColor: keyEventsOpen ? '#ece7fd' : 'transparent',
                    color: '#342c82',
                    fontWeight: 500,
                    fontFamily: 'Figtree, sans-serif',
                    fontSize: '13px',
                    padding: '6px 12px',
                  }}
                >
                  <Calendar size={16} />
                  <span>Key Events</span>
                </div>
              </KeyEventsPopover>
            )}
            {entityType === "projectTimeline" && (
              <CriticalPathButton
                projectId={entityID}
                active={criticalPathHighlight}
                onToggle={() => setCriticalPathHighlight((prev) => !prev)}
              />
            )}
            <CustomButton
              btnClass="todayBtn"
              label="Today"
              name="today"
              id="todayBtn"
              clickHandler={() => {
                setTodayTrigger(false);
                setTimeout(() => {
                  setTodayTrigger(true);
                }, 0);
              }}
            />
            <CustomPopover
              icon={
                <CustomButton
                  icon={selection.icon}
                  label={selection.name}
                  btnClass={`${styles.calendarBtn} d_btn`}
                  rightIcon={
                    <span className="ms-6 d-inline-flex">
                      <ArrowDownMedium />
                    </span>
                  }
                  clickHandler={() =>
                    setIsOpenPopover(openPopover === "range" ? null : "range")
                  }
                />
              }
              width="96px"
              PopSurfaceClass={styles.calendarPopover}
              status={openPopover === "range"}
            >
              {entityType === "portfolio" && (
                <div
                  className={styles.calendarPopoverItem}
                  onClick={() => {
                    setTabView("year");
                    setSelection({ icon: <YearIcon />, name: "Year" });
                    setCount((prev) => prev + 1);
                    setIsOpenPopover(null);
                  }}
                >
                  <YearIcon />
                  Year
                </div>
              )}

              <div
                className={styles.calendarPopoverItem}
                onClick={() => {
                  setTabView("quarter");
                  setSelection({ icon: <QuarterIcon />, name: "Quarter" });
                  setCount((prev) => prev + 1);
                  setIsOpenPopover(null);
                }}
              >
                <QuarterIcon />
                Quarter
              </div>

              <div
                className={styles.calendarPopoverItem}
                onClick={() => {
                  setTabView("month");
                  setSelection({ icon: <MonthIcon />, name: "Month" });
                  setCount((prev) => prev + 1);
                  setIsOpenPopover(null);
                }}
              >
                <MonthIcon />
                Month
              </div>
              {entityType !== "portfolio" &&
                <div
                  className={styles.calendarPopoverItem}
                  onClick={() => {
                    setSelection({ icon: <WeekIcon />, name: "Week" });
                    setCount((prev) => prev + 1);
                    setIsOpenPopover(null);
                    setTabView("week");
                  }}
                >
                  <WeekIcon />
                  Week
                </div>
              }
              {entityType !== "portfolio" &&
                <div
                  className={styles.calendarPopoverItem}
                  onClick={() => {
                    setSelection({ icon: <DayIcon />, name: "Day" });
                    setCount((prev) => prev + 1);
                    setIsOpenPopover(null);
                    setTabView("day");
                  }}
                >
                  <DayIcon />
                  Day
                </div>
              }
            </CustomPopover>
          </div>
        </div>
        <GanttChart
          isHide={isHideSideBar}
          viewType={selection.name}
          count={count}
          isFullHeight={isFullHeight}
          entityType={entityType}
          keyEvents={isKeyEventCapable ? keyEvents : []}
        />
      </div>
    </div>
  );
};

export default connect(null, null)(Timeline);